

"use client";

import { useState } from "react";
import styles from "../HealthChat.module.css";
import { formatChatTime } from "../../../utils/index";

/* ─────────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────────── */

const PulseIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const BrainIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d="M9.5 2a4.5 4.5 0 0 1 4.5 4v1h1a3 3 0 0 1 3 3 3 3 0 0 1-1.5 2.6V14a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4v-1.4A3 3 0 0 1 5.5 10a3 3 0 0 1 3-3H9V6a4.5 4.5 0 0 1 .5-4z" />
  </svg>
);

const HeartIcon = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */

const ASSESSMENTS = [
  {
    id: "pcos",
    label: "PCOS Risk Assessment",
    desc: "Evaluate symptoms and hormonal health risks",
    icon: PulseIcon,
    enabled: true,

    intro:
      "This assessment will help us understand your risk for PCOS based on your symptoms and health history.",

    result: {
      title: "Moderate Risk for PCOS",
      score: 60,
      desc:
        "Your responses indicate symptoms commonly associated with PCOS. We recommend consulting a healthcare specialist for personalized guidance.",
      factors: [
        "Irregular menstrual cycles",
        "Acne",
        "Weight gain",
        "Excess hair growth",
      ],
    },

    recommendations: [
      {
        id: "pcos-program",
        title: "Hormonal Balance Program",
        desc:
          "Personalized hormone wellness plan with nutrition and lifestyle support.",
        icon: "🌿",
        iconBg: "#EDE9FE",
        type: "Program",
        typeColor: "#6B21A8",
        typeBg: "#F3E8FF",
      },
      {
        id: "nutrition",
        title: "Nutrition for PCOS",
        desc:
          "Improve metabolism and hormonal balance with expert meal guidance.",
        icon: "🥗",
        iconBg: "#FEF3C7",
        type: "Nutrition",
        typeColor: "#B45309",
        typeBg: "#FEF3C7",
      },
    ],

    questions: [
      {
        id: 1,
        question: "How regular are your menstrual cycles?",
        options: [
          "Very regular",
          "Sometimes irregular",
          "Often irregular",
          "Absent for months",
        ],
      },
      {
        id: 2,
        question: "Have you experienced sudden weight gain?",
        options: ["No", "Mild", "Moderate", "Significant"],
      },
      {
        id: 3,
        question: "Do you experience acne or oily skin?",
        options: [
          "Never",
          "Occasionally",
          "Frequently",
          "Severe",
        ],
      },
    ],
  },

  {
    id: "mental",
    label: "Mental Health (PHQ-9)",
    desc: "Depression and emotional wellbeing screening",
    icon: BrainIcon,
    enabled: true,

    intro:
      "This screening helps evaluate your emotional wellbeing and identify signs of stress, anxiety, or depression.",

    result: {
      title: "Mild Depression Risk",
      score: 42,
      desc:
        "Your responses suggest mild emotional distress. Talking with a mental health professional may help improve your wellbeing.",
      factors: [
        "Low energy",
        "Poor sleep",
        "Stress",
        "Mood changes",
      ],
    },

    recommendations: [
      {
        id: "therapy",
        title: "Mental Wellness Program",
        desc:
          "Guided emotional wellness support and self-care plans.",
        icon: "🧠",
        iconBg: "#E0F2FE",
        type: "Program",
        typeColor: "#0369A1",
        typeBg: "#E0F2FE",
      },
    ],

    questions: [
      {
        id: 1,
        question:
          "How often have you felt little interest in doing things?",
        options: [
          "Not at all",
          "Several days",
          "More than half the days",
          "Nearly every day",
        ],
      },
      {
        id: 2,
        question:
          "How often have you felt down or hopeless?",
        options: [
          "Not at all",
          "Several days",
          "More than half the days",
          "Nearly every day",
        ],
      },
    ],
  },

  {
    id: "menopause",
    label: "Menopause Assessment",
    desc: "Assess transition stage and symptom severity",
    icon: HeartIcon,
    enabled: true,

    intro:
      "This assessment evaluates menopause-related symptoms and hormonal transition patterns.",

    result: {
      title: "Perimenopause Symptoms Detected",
      score: 55,
      desc:
        "Your responses suggest possible perimenopause symptoms that may benefit from lifestyle and medical support.",
      factors: [
        "Hot flashes",
        "Mood swings",
        "Sleep issues",
      ],
    },

    recommendations: [
      {
        id: "menopause-care",
        title: "Menopause Care Plan",
        desc:
          "Hormonal and lifestyle support for menopause symptom relief.",
        icon: "💜",
        iconBg: "#F3E8FF",
        type: "Care",
        typeColor: "#7E22CE",
        typeBg: "#F3E8FF",
      },
    ],

    questions: [
      {
        id: 1,
        question:
          "Have you been experiencing hot flashes?",
        options: [
          "Never",
          "Occasionally",
          "Frequently",
          "Severe",
        ],
      },
      {
        id: 2,
        question:
          "How often do you experience sleep disturbances?",
        options: [
          "Never",
          "Sometimes",
          "Often",
          "Every night",
        ],
      },
    ],
  },

  {
    id: "fertility",
    label: "Fertility Assessment",
    desc: "Check reproductive and fertility health",
    icon: PulseIcon,
    enabled: true,

    intro:
      "This assessment helps evaluate fertility health and reproductive wellness.",

    result: {
      title: "Fertility Health Needs Attention",
      score: 48,
      desc:
        "Your responses suggest areas where fertility support and medical guidance may help.",
      factors: [
        "Cycle irregularity",
        "Stress",
        "Lifestyle factors",
      ],
    },

    recommendations: [
      {
        id: "fertility",
        title: "Fertility Wellness",
        desc:
          "Specialized fertility and reproductive care guidance.",
        icon: "🌸",
        iconBg: "#FCE7F3",
        type: "Care",
        typeColor: "#BE185D",
        typeBg: "#FCE7F3",
      },
    ],

    questions: [
      {
        id: 1,
        question:
          "How long have you been trying to conceive?",
        options: [
          "Not trying",
          "Less than 6 months",
          "6–12 months",
          "More than 1 year",
        ],
      },
      {
        id: 2,
        question:
          "Do you track ovulation regularly?",
        options: [
          "Always",
          "Sometimes",
          "Rarely",
          "Never",
        ],
      },
    ],
  },

  {
    id: "sleep",
    label: "Sleep Quality",
    desc: "Understand your sleep health and recovery",
    icon: BrainIcon,
    enabled: false,
  },

  {
    id: "lifestyle",
    label: "Lifestyle Risk",
    desc: "Assess daily habits impacting your health",
    icon: HeartIcon,
    enabled: true,

    intro:
      "This assessment helps understand lifestyle habits impacting your overall health.",

    result: {
      title: "Lifestyle Risk Detected",
      score: 50,
      desc:
        "Certain lifestyle habits may be affecting your physical and emotional wellbeing.",
      factors: [
        "Low activity",
        "Poor sleep",
        "High stress",
      ],
    },

    recommendations: [
      {
        id: "lifestyle-care",
        title: "Lifestyle Transformation",
        desc:
          "Daily habit coaching for holistic wellness improvement.",
        icon: "🏃‍♀️",
        iconBg: "#DCFCE7",
        type: "Wellness",
        typeColor: "#15803D",
        typeBg: "#DCFCE7",
      },
    ],

    questions: [
      {
        id: 1,
        question:
          "How often do you exercise weekly?",
        options: [
          "Daily",
          "3-4 times",
          "1-2 times",
          "Rarely",
        ],
      },
      {
        id: 2,
        question:
          "How would you rate your stress levels?",
        options: [
          "Low",
          "Moderate",
          "High",
          "Very high",
        ],
      },
    ],
  },
];

const COMMON_RECOMMENDATIONS = [
  {
    id: "consult",
    title: "Consult a Gynecologist",
    desc:
      "Discuss your symptoms and next steps",
    icon: "🌸",
    iconBg: "#FDF2F8",
    iconColor: "#EC4899",
  },
  {
    id: "program",
    title: "PCOS Care Program",
    desc:
      "Personalised program to manage symptoms and improve health",
    icon: "🧘‍♀️",
    iconBg: "#ECFDF5",
    iconColor: "#10B981",
  },
  {
    id: "nutrition",
    title: "Nutrition Plan",
    desc:
      "Balanced diet plan tailored for hormonal health",
    icon: "🥗",
    iconBg: "#FFF7ED",
    iconColor: "#F97316",
  },
  {
    id: "fitness",
    title: "Lifestyle & Fitness",
    desc:
      "Workouts and lifestyle tips for better wellbeing",
    icon: "🏃‍♀️",
    iconBg: "#F5F3FF",
    iconColor: "#8B5CF6",
  },
];

/* ─────────────────────────────────────────────────────────
   SELECT SCREEN
───────────────────────────────────────────────────────── */

function AssessmentSelectScreen({
  visibleCount,
  setVisibleCount,
  onSelect,
  onBack,
}) {
  const remaining = Math.max(
    ASSESSMENTS.length - visibleCount,
    0
  );
const STEP_CONFIG = {
  title: "Health Programs",
  subtitle: "Explore our programs designed for your wellbeing",
};
  return (
    <div className={styles.assessWrap}>
      <div className={styles.assessHeader}>
        <button
          className={styles.assessBackBtn}
          onClick={onBack}
        >
          ← Back
        </button>
      </div>

      {/* <div className={styles.assessTitle}>
        Take Assessment
      </div>

      <div className={styles.assessSub}>
        Select a screening to evaluate your
        health risks
      </div> */}
 <div className={styles.symptomPickerBotRow}>
        <img src="/images/chatbotFemCare.svg" alt="bot" width={48} height={48} className={styles.symptomPickerBotImg} />
        <div className={`${styles.symptomPickerBotBubble} ${styles.CBbodyText}`}>
          <strong>{STEP_CONFIG.title}</strong>
          <p style={{ fontSize: "0.85rem", margin: 0, opacity: 0.9 }}>
            {STEP_CONFIG.subtitle}
          </p>
        </div>
      </div>
      <div className={styles.assessGrid}>
        {ASSESSMENTS.map(
          (item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                disabled={!item.enabled}
                onClick={() =>
                  item.enabled && onSelect(item)
                }
                className={`${styles.assessCard} ${
                  !item.enabled
                    ? styles.assessDisabled
                    : ""
                }`}
              >
                {!item.enabled && (
                  <div className={styles.assessSoon}>
                    Coming Soon
                  </div>
                )}

                <div className={styles.assessIcon}>
                  <Icon />
                </div>

                <div className={styles.assessCardTitle}>
                  {item.label}
                </div>
              </button>
            );
          }
        )}
      </div>

      {/* {visibleCount < ASSESSMENTS.length && (
        <button
          className={styles.assessLoadBtn}
          onClick={() =>
            setVisibleCount((p) =>
              Math.min(
                p + 4,
                ASSESSMENTS.length
              )
            )
          }
        >
          Load More ({remaining})
        </button>
      )} */}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   INTRO SCREEN
───────────────────────────────────────────────────────── */

function AssessmentIntroScreen({
  assessment,
  onContinue,
  onBack,
}) {
  return (
    <div className={styles.assessWrap}>
      <div className={styles.assessHeader}>
        <button
          className={styles.assessBackBtn}
          onClick={onBack}
        >
          ← Back
        </button>
      </div>

      <div className={styles.assessChatRow}>
        <img
          src="/images/chatbotFemCare.svg"
          alt="bot"
          width={52}
          height={52}
          className={styles.assessAvatar}
        />

        <div>
          <div className={styles.assessChatBubble}>
            {assessment?.intro}
          </div>

          <div className={styles.assessTime}>
            {formatChatTime()}
          </div>
        </div>
      </div>

      <div className={styles.assessHero}>
        <img
          src="/images/assessintro.svg"
          alt="assessment"
          className={styles.assessHeroImg}
        />
      </div>

      <div className={styles.assessInfoCard}>
        <div className={styles.assessInfoRow}>
          <span>Assessment</span>
          <strong>{assessment?.label}</strong>
        </div>

        <div className={styles.assessInfoRow}>
          <span>Questions</span>
          <strong>
            {assessment?.questions?.length}
          </strong>
        </div>

        <div className={styles.assessInfoRow}>
          <span>Type</span>
          <strong>Interactive</strong>
        </div>

        <div className={styles.assessInfoRow}>
          <span>Privacy</span>
          <strong>100% confidential</strong>
        </div>
      </div>

      <div className={styles.assessPrivacyCard}>
        <div className={styles.assessShield}>
          🛡️
        </div>

        <div className={styles.assessPrivacyText}>
          Your responses are secure and
          will only be used to personalize
          your care.
        </div>
      </div>

      <button
        className={styles.assessPrimaryBtn}
        onClick={onContinue}
      >
        Start Assessment
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   QUESTION SCREEN
───────────────────────────────────────────────────────── */

function AssessmentQuestionScreen({
  assessment,
  questionIndex,
  selected,
  setSelected,
  onNext,
  onPrev,
}) {
  const questions = assessment?.questions || [];
  const q = questions[questionIndex];

  const answeredCount = selected
    ? questionIndex + 1
    : questionIndex;

  const progress =
    (answeredCount / questions.length) * 100;

  return (
    <div className={styles.assessWrap}>
      <div className={styles.assessTopBar}>
        <button
          className={styles.assessIconBtn}
          onClick={onPrev}
        >
          ←
        </button>

        <div className={styles.assessTopTitle}>
          {assessment?.label}
        </div>
      </div>

      <div className={styles.assessProgressWrap}>
        <div className={styles.assessProgressText}>
          Question {questionIndex + 1} of{" "}
          {questions.length}
        </div>

        <div className={styles.assessProgressBar}>
          <div
            className={styles.assessProgressFill}
            style={{
              width: `${progress}%`,
            }}
          />
        </div>
      </div>

      <div className={styles.assessChatRow}>
        <img
          src="/images/chatbotFemCare.svg"
          alt="bot"
          width={52}
          height={52}
          className={styles.assessAvatar}
        />

        <div className={styles.assessChatBubble}>
          Let’s understand your symptoms
          better 💜
        </div>
      </div>

      <div className={styles.assessQuestionCard}>
        <div className={styles.assessQuestion}>
          {q.question}
        </div>

        <div className={styles.assessOptions}>
          {q.options.map((opt) => (
            <button
              key={opt}
              onClick={() => setSelected(opt)}
              className={`${styles.assessOptionBtn} ${
                selected === opt
                  ? styles.assessOptionActive
                  : ""
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.assessFooterBtns}>
        <button
          className={styles.assessOutlineBtn}
          onClick={onPrev}
        >
          Back
        </button>

        <button
          disabled={!selected}
          className={styles.assessPrimaryBtn}
          onClick={onNext}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   RESULTS SCREEN
───────────────────────────────────────────────────────── */

function AssessmentResultsScreen({
  assessment,
  onContinue,
  onBack,
}) {
  return (
    <div className={styles.assessWrap}>
      <div className={styles.assessHeader}>
        <button
          className={styles.assessBackBtn}
          onClick={onBack}
        >
          ← Back
        </button>
      </div>

      <div className={styles.assessChatRow}>
        <img
          src="/images/chatbotFemCare.svg"
          alt="bot"
          width={52}
          height={52}
          className={styles.assessAvatar}
        />

        <div>
          <div className={styles.assessChatBubble}>
            Here are your results. Based on
            your responses, you’re at:
          </div>

          <div className={styles.assessTime}>
            {formatChatTime()}
          </div>
        </div>
      </div>

      <div className={styles.assessResultCard}>
        <div className={styles.assessRiskTitle}>
          {assessment?.result?.title}
        </div>

        <div className={styles.assessGaugeWrap}>
          <div className={styles.assessGauge}>
            <div className={styles.assessGaugeInner}>
              <div className={styles.assessGaugePercent}>
                {assessment?.result?.score}%
              </div>

              <div className={styles.assessGaugeLabel}>
                Risk Score
              </div>
            </div>
          </div>
        </div>

        <div className={styles.assessResultDesc}>
          {assessment?.result?.desc}
        </div>

        <div className={styles.assessFactorTitle}>
          Your top contributing factors
        </div>

        <ul className={styles.assessFactorList}>
          {assessment?.result?.factors?.map(
            (factor) => (
              <li key={factor}>{factor}</li>
            )
          )}
        </ul>

        <button
          className={styles.assessPrimaryBtn}
          onClick={onContinue}
        >
          Continue to Recommendations
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   RECOMMENDATION SCREEN
───────────────────────────────────────────────────────── */

function AssessmentRecommendationsScreen({
  assessment,
  onRestart,
  onBack,
}) {
  return (
    <div className={styles.assessWrap}>
      <div className={styles.assessHeader}>
        <button
          className={styles.assessBackBtn}
          onClick={onBack}
        >
          ← Back
        </button>
      </div>

      <div className={styles.assessChatRow}>
        <img
          src="/images/chatbotFemCare.svg"
          alt="Chatbot"
          width={52}
          height={52}
          className={styles.assessAvatar}
        />

        <div>
          <div className={styles.assessChatBubble}>
            Here are personalized
            recommendations for you 💜
          </div>

          <div className={styles.assessTime}>
            {formatChatTime()}
          </div>
        </div>
      </div>

      <div className={styles.assessSectionTitle}>
        Top Recommendations
      </div>

      <div className={styles.assessRecList}>
        {COMMON_RECOMMENDATIONS?.map(
          (rec) => (
            <a
              key={rec.id}
              href="#"
              className={styles.assessRecCard}
            >
              <div
                className={styles.assessRecIcon}
                style={{
                  background: rec.iconBg,
                }}
              >
                {rec.icon}
              </div>

              <div
                className={
                  styles.assessRecContent
                }
              >
                <div
                  className={
                    styles.assessRecTitle
                  }
                >
                  {rec.title}
                </div>

                <div
                  className={
                    styles.assessRecDesc
                  }
                >
                  {rec.desc}
                </div>
              </div>

               <div
          className={styles.assessRecArrow}
        >
          ›
        </div>
            </a>
          )
        )}
      </div>

      <div className={styles.assessExpertCard}>
        <div className={styles.assessExpertAvatar}>
          👩‍⚕️
        </div>

        <div>
          <div className={styles.assessExpertTitle}>
            Need help understanding your
            results?
          </div>

          <div className={styles.assessExpertDesc}>
            Our care experts are always
            here to guide you.
          </div>
        </div>
      </div>

      <div className={styles.assessExpertBtnRow}>
        <button
          className={styles.assessOutlineBtn}
          onClick={onRestart}
        >
          Restart
        </button>
        <button
          className={styles.assessPrimaryBtn}
        >
          Talk to Expert
        </button>

        
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   ROOT
───────────────────────────────────────────────────────── */

export default function HealthProgramsScreen({
  onBack,
}) {
  const [screen, setScreen] =
    useState("select");

  const [selectedAssessment, setSelectedAssessment] =
    useState(null);

  const [visibleCount, setVisibleCount] =
    useState(4);

  const [questionIndex, setQuestionIndex] =
    useState(0);

  const [selected, setSelected] =
    useState("");

  const handleNextQuestion = () => {
    if (
      questionIndex <
      selectedAssessment.questions.length - 1
    ) {
      setQuestionIndex((p) => p + 1);
      setSelected("");
    } else {
      setScreen("results");
    }
  };

  return (
    <>
      {screen === "select" && (
        <AssessmentSelectScreen
          visibleCount={visibleCount}
          setVisibleCount={setVisibleCount}
          onSelect={(assessment) => {
            setSelectedAssessment(
              assessment
            );
            setScreen("intro");
          }}
          onBack={onBack}
        />
      )}

      {screen === "intro" && (
        <AssessmentIntroScreen
          assessment={selectedAssessment}
          onContinue={() =>
            setScreen("questions")
          }
          onBack={() =>
            setScreen("select")
          }
        />
      )}

      {screen === "questions" && (
        <AssessmentQuestionScreen
          assessment={selectedAssessment}
          questionIndex={questionIndex}
          selected={selected}
          setSelected={setSelected}
          onNext={handleNextQuestion}
          onPrev={() => {
            if (questionIndex === 0) {
              setScreen("intro");
            } else {
              setQuestionIndex(
                (p) => p - 1
              );
            }
          }}
        />
      )}

      {screen === "results" && (
        <AssessmentResultsScreen
          assessment={selectedAssessment}
          onContinue={() =>
            setScreen("recommendations")
          }
          onBack={() =>
            setScreen("questions")
          }
        />
      )}

      {screen === "recommendations" && (
        <AssessmentRecommendationsScreen
          assessment={selectedAssessment}
          onBack={() =>
            setScreen("results")
          }
          onRestart={() => {
            setScreen("select");
            setQuestionIndex(0);
            setSelected("");
            setSelectedAssessment(null);
          }}
        />
      )}
    </>
  );
}






// "use client";

// import { useState } from "react";
// import styles from "../HealthChat.module.css";
// import { formatChatTime } from "../../../utils/index";

// /* ─────────────────────────────────────────────────────────
//    ICONS
// ───────────────────────────────────────────────────────── */

// const PulseIcon = ({ size = 24 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.8"
//   >
//     <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
//   </svg>
// );

// const BrainIcon = ({ size = 24 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.8"
//   >
//     <path d="M9.5 2a4.5 4.5 0 0 1 4.5 4v1h1a3 3 0 0 1 3 3 3 3 0 0 1-1.5 2.6V14a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4v-1.4A3 3 0 0 1 5.5 10a3 3 0 0 1 3-3H9V6a4.5 4.5 0 0 1 .5-4z" />
//   </svg>
// );

// const HeartIcon = ({ size = 24 }) => (
//   <svg
//     width={size}
//     height={size}
//     viewBox="0 0 24 24"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="1.8"
//   >
//     <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
//   </svg>
// );

// /* ─────────────────────────────────────────────────────────
//    DATA
// ───────────────────────────────────────────────────────── */

// const ASSESSMENTS = [
//   {
//     id: "pcos",
//     label: "PCOS Risk Assessment",
//     desc: "Evaluate symptoms and hormonal health risks",
//     icon: PulseIcon,
//     enabled: true,
//   },
//   {
//     id: "mental",
//     label: "Mental Health (PHQ-9)",
//     desc: "Depression and emotional wellbeing screening",
//     icon: BrainIcon,
//     enabled: true,
//   },
//   {
//     id: "menopause",
//     label: "Menopause Assessment",
//     desc: "Assess transition stage and symptom severity",
//     icon: HeartIcon,
//     enabled: true,
//   },
//   {
//     id: "fertility",
//     label: "Fertility Assessment",
//     desc: "Check reproductive and fertility health",
//     icon: PulseIcon,
//     enabled: true,
//   },
//   {
//     id: "sleep",
//     label: "Sleep Quality",
//     desc: "Understand your sleep health and recovery",
//     icon: BrainIcon,
//     enabled: false,
//   },
//   {
//     id: "lifestyle",
//     label: "Lifestyle Risk",
//     desc: "Assess daily habits impacting your health",
//     icon: HeartIcon,
//     enabled: true,
//   },
// ];

// const QUESTIONS = [
//   {
//     id: 1,
//     question: "How regular are your menstrual cycles?",
//     options: [
//       "Very regular",
//       "Sometimes irregular",
//       "Often irregular",
//       "Absent for months",
//     ],
//   },
//   {
//     id: 2,
//     question: "Have you experienced sudden weight gain?",
//     options: [
//       "No",
//       "Mild",
//       "Moderate",
//       "Significant",
//     ],
//   },
//   {
//     id: 3,
//     question: "Do you experience acne or oily skin?",
//     options: [
//       "Never",
//       "Occasionally",
//       "Frequently",
//       "Severe",
//     ],
//   },
// ];

// /* ─────────────────────────────────────────────────────────
//    SELECT SCREEN
// ───────────────────────────────────────────────────────── */

// function AssessmentSelectScreen({
//   visibleCount,
//   setVisibleCount,
//   onSelect,
//   onBack,
// }) {
//   const remaining = Math.max(
//     ASSESSMENTS.length - visibleCount,
//     0
//   );

//   return (
//     <div className={styles.assessWrap}>

//       {/* HEADER */}
//       <div className={styles.assessHeader}>
//         <button
//           className={styles.assessBackBtn}
//           onClick={onBack}
//         >
//           ← Back
//         </button>
//       </div>

//       <div className={styles.assessTitle}>
//         Take Assessment
//       </div>

//       <div className={styles.assessSub}>
//         Select a screening to evaluate your
//         health risks
//       </div>

//       <div className={styles.assessGrid}>
//         {ASSESSMENTS.slice(0, visibleCount).map(
//           (item) => {
//             const Icon = item.icon;

//             return (
//               <button
//                 key={item.id}
//                 disabled={!item.enabled}
//                 onClick={() =>
//                   item.enabled && onSelect()
//                 }
//                 className={`${styles.assessCard} ${
//                   !item.enabled
//                     ? styles.assessDisabled
//                     : ""
//                 }`}
//               >
//                 {!item.enabled && (
//                   <div className={styles.assessSoon}>
//                     Coming Soon
//                   </div>
//                 )}

//                 <div className={styles.assessIcon}>
//                   <Icon />
//                 </div>

//                 <div className={styles.assessCardTitle}>
//                   {item.label}
//                 </div>

//                 {/* <div className={styles.assessCardDesc}>
//                   {item.desc}
//                 </div> */}
//               </button>
//             );
//           }
//         )}
//       </div>

//       {visibleCount < ASSESSMENTS.length && (
//         <button
//           className={styles.assessLoadBtn}
//           onClick={() =>
//             setVisibleCount((p) =>
//               Math.min(
//                 p + 4,
//                 ASSESSMENTS.length
//               )
//             )
//           }
//         >
//           Load More ({remaining})
//         </button>
//       )}
//     </div>
//   );
// }

// /* ─────────────────────────────────────────────────────────
//    INTRO SCREEN
// ───────────────────────────────────────────────────────── */

// function AssessmentIntroScreen({ onContinue, onBack }) {
//   return (
//     <div className={styles.assessWrap}>
//       <div className={styles.assessHeader}>
//         <button
//           className={styles.assessBackBtn}
//           onClick={onBack}
//         >
//           ← Back
//         </button>
//       </div>

//       <div className={styles.assessChatRow}>
//         <img
//           src="/images/chatbotFemCare.svg"
//           alt="bot"
//           width={52}
//           height={52}
//           className={styles.assessAvatar}
//         />

//         <div>
//           <div className={styles.assessChatBubble}>
//             This assessment will help us
//             understand your risk for PCOS
//             based on your symptoms and
//             health history.
//           </div>

//           <div className={styles.assessTime}>
//             10:30 AM
//           </div>
//         </div>
//       </div>

//       <div className={styles.assessHero}>
//         <img
//           src="/images/assessintro.svg"
//           alt="assessment"
//           className={styles.assessHeroImg}
//         />
//       </div>

//       <div className={styles.assessInfoCard}>
//         <div className={styles.assessInfoRow}>
//           <span>Duration</span>
//           <strong>10–15 minutes</strong>
//         </div>

//         <div className={styles.assessInfoRow}>
//           <span>Questions</span>
//           <strong>20–25 questions</strong>
//         </div>

//         <div className={styles.assessInfoRow}>
//           <span>Type</span>
//           <strong>Interactive</strong>
//         </div>

//         <div className={styles.assessInfoRow}>
//           <span>Privacy</span>
//           <strong>100% confidential</strong>
//         </div>
//       </div>

//       <div className={styles.assessPrivacyCard}>
//         <div className={styles.assessShield}>
//           🛡️
//         </div>

//         <div className={styles.assessPrivacyText}>
//           Your responses are secure and
//           will only be used to personalize
//           your care.
//         </div>
//       </div>

//       <button
//         className={styles.assessPrimaryBtn}
//         onClick={onContinue}
//       >
//         Start Assessment
//       </button>
//     </div>
//   );
// }

// /* ─────────────────────────────────────────────────────────
//    QUESTION SCREEN
// ───────────────────────────────────────────────────────── */

// function AssessmentQuestionScreen({
//   questionIndex,
//   selected,
//   setSelected,
//   onNext,
//   onPrev,
// }) {
//   const q = QUESTIONS[questionIndex];
//   // const progress =
//   //   ((questionIndex + 1) /
//   //     QUESTIONS.length) *
//   //   100;

//     const answeredCount = selected
//       ? questionIndex + 1
//       : questionIndex;

// const progress =
//   (answeredCount / QUESTIONS.length) * 100;

//   return (
//     <div className={styles.assessWrap}>

//       <div className={styles.assessTopBar}>
//         <button
//           className={styles.assessIconBtn}
//           onClick={onPrev}
//         >
//           ←
//         </button>

//         <div className={styles.assessTopTitle}>
//           PCOS Risk Assessment
//         </div>

        
//       </div>

//       <div className={styles.assessProgressWrap}>
//         <div className={styles.assessProgressText}>
//           Question {questionIndex + 1} of{" "}
//           {QUESTIONS.length}
//         </div>

//         <div className={styles.assessProgressBar}>
//           <div
//             className={styles.assessProgressFill}
//             style={{
//               width: `${progress}%`,
//             }}
//           />
//         </div>
//       </div>


//       <div className={styles.assessChatRow}>
//         <img
//           src="/images/chatbotFemCare.svg"
//           alt="bot"
//           width={52}
//           height={52}
//           className={styles.assessAvatar}
//         />
//         <div className={styles.assessChatBubble}>
//         Let’s understand your symptoms
//         better 💜
//       </div>

//         </div>
      

//       <div className={styles.assessQuestionCard}>
//         <div className={styles.assessQuestion}>
//           {q.question}
//         </div>

//         <div className={styles.assessOptions}>
//           {q.options.map((opt) => (
//             <button
//               key={opt}
//               onClick={() => setSelected(opt)}
//               className={`${styles.assessOptionBtn} ${
//                 selected === opt
//                   ? styles.assessOptionActive
//                   : ""
//               }`}
//             >
//               {opt}
//             </button>
//           ))}
//         </div>
//       </div>

//       <div className={styles.assessFooterBtns}>
//         <button
//           className={styles.assessOutlineBtn}
//           onClick={onPrev}
//         >
//           Back
//         </button>

//         <button
//           disabled={!selected}
//           className={styles.assessPrimaryBtn}
//           onClick={onNext}
//         >
//           Continue
//         </button>
//       </div>
//     </div>
//   );
// }

// /* ─────────────────────────────────────────────────────────
//    RESULTS SCREEN
// ───────────────────────────────────────────────────────── */

// function AssessmentResultsScreen({
//   onContinue,
//   onBack
// }) {
//   return (
//     <div className={styles.assessWrap}>

//       <div className={styles.assessHeader}>
//         <button
//           className={styles.assessBackBtn}
//           onClick={onBack}
//         >
//           ← Back
//         </button>
//       </div>

//       <div className={styles.assessChatRow}>
//         <img
//           src="/images/chatbotFemCare.svg"
//           alt="bot"
//           width={52}
//           height={52}
//           className={styles.assessAvatar}
//         />

//         <div>
//           <div className={styles.assessChatBubble}>
//             Here are your results. Based on
//             your responses, you’re at:
//           </div>

//           <div className={styles.assessTime}>
//             {formatChatTime()}
//           </div>
//         </div>
//       </div>

//       <div className={styles.assessResultCard}>
//         <div className={styles.assessRiskTitle}>
//           Moderate Risk for PCOS
//         </div>

//         <div className={styles.assessGaugeWrap}>
//           <div className={styles.assessGauge}>
//             <div className={styles.assessGaugeInner}>
//               <div className={styles.assessGaugePercent}>
//                 60%
//               </div>

//               <div className={styles.assessGaugeLabel}>
//                 Risk Score
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className={styles.assessResultDesc}>
//           Your responses indicate symptoms
//           commonly associated with PCOS.
//           We recommend consulting a
//           healthcare specialist for
//           personalized guidance.
//         </div>

//         <div className={styles.assessFactorTitle}>
//           Your top contributing factors
//         </div>

//         <ul className={styles.assessFactorList}>
//           <li>Irregular menstrual cycles</li>
//           <li>Acne</li>
//           <li>Weight gain</li>
//           <li>Excess hair growth</li>
//         </ul>

//         {/* <button
//           className={styles.assessPrimaryBtn}
//           onClick={onContinue}
//         >
//           📄 View Detailed Report
//         </button> */}
//         <button
//           className={styles.assessPrimaryBtn}
//           onClick={onContinue}
//         >
//           Continue to Recommendations
//         </button>
//       </div>
//     </div>
//   );
// }


// function AssessmentRecommendationsScreen({
//   onRestart,
//   onBack,
// }) {
//   const RECOMMENDATIONS = [
//     {
//       id: "pcos-program",
//       title: "Hormonal Balance Program",
//       desc:
//         "Personalized hormone wellness plan with nutrition and lifestyle support.",
//       icon: "🌿",
//       iconBg: "#EDE9FE",
//       type: "Program",
//       typeColor: "#6B21A8",
//       typeBg: "#F3E8FF",
//       link: "#",
//     },
//     {
//       id: "nutrition",
//       title: "Nutrition for PCOS",
//       desc:
//         "Improve metabolism and hormonal balance with expert meal guidance.",
//       icon: "🥗",
//       iconBg: "#FEF3C7",
//       type: "Nutrition",
//       typeColor: "#B45309",
//       typeBg: "#FEF3C7",
//       link: "#",
//     },
//     {
//       id: "consult",
//       title: "Consult a Gynecologist",
//       desc:
//         "Talk to a specialist for deeper evaluation and personalized care.",
//       icon: "👩‍⚕️",
//       iconBg: "#FCE7F3",
//       type: "Consult",
//       typeColor: "#BE185D",
//       typeBg: "#FCE7F3",
//       link: "#",
//     },
//   ];

//   return (
//     <div className={styles.assessWrap}>

//       {/* HEADER */}
//       <div className={styles.assessHeader}>
//         <button
//           className={styles.assessBackBtn}
//           onClick={onBack}
//         >
//           ← Back
//         </button>
//       </div>

//       {/* CHAT */}
//       <div className={styles.assessChatRow}>
//         <img
//           src="/images/chatbotFemCare.svg"
//           alt="Chatbot"
//           width={52}
//           height={52}
//           className={styles.assessAvatar}
//         />

//         <div>
//           <div className={styles.assessChatBubble}>
//             Here are personalized
//             recommendations for you 💜
//           </div>

//           <div className={styles.assessTime}>
//             10:34 AM
//           </div>
//         </div>
//       </div>

//       {/* TITLE */}
//       <div className={styles.assessSectionTitle}>
//         Top Recommendations
//       </div>

//       {/* RECOMMENDATIONS */}
//       <div className={styles.assessRecList}>
//         {RECOMMENDATIONS.map((rec) => (
//           <a
//             key={rec.id}
//             href={rec.link}
//             className={styles.assessRecCard}
//           >
//             <div
//               className={styles.assessRecIcon}
//               style={{
//                 background: rec.iconBg,
//               }}
//             >
//               {rec.icon}
//             </div>

//             <div className={styles.assessRecContent}>
//               <div className={styles.assessRecTitle}>
//                 {rec.title}
//               </div>

//               <div className={styles.assessRecDesc}>
//                 {rec.desc}
//               </div>
//             </div>

//             <span
//               className={styles.assessRecBadge}
//               style={{
//                 color: rec.typeColor,
//                 background: rec.typeBg,
//               }}
//             >
//               {rec.type} ›
//             </span>
//           </a>
//         ))}
//       </div>

//       {/* EXPERT CARD */}
//       <div className={styles.assessExpertCard}>
//         <div className={styles.assessExpertAvatar}>
//           👩‍⚕️
//         </div>

//         <div>
//           <div className={styles.assessExpertTitle}>
//             Need help understanding your
//             results?
//           </div>

//           <div className={styles.assessExpertDesc}>
//             Our care experts are always
//             here to guide you.
//           </div>
//         </div>
//       </div>

//       {/* ACTIONS */}
//       <div className={styles.assessExpertBtnRow}>
//         <button className={styles.assessPrimaryBtn}>
//           Talk to Expert
//         </button>

//         <button
//           className={styles.assessOutlineBtn}
//           onClick={onRestart}
//         >
//           Restart
//         </button>
//       </div>
//     </div>
//   );
// }

// /* ─────────────────────────────────────────────────────────
//    ROOT
// ───────────────────────────────────────────────────────── */

// export default function HealthProgramsScreen({
//   onBack,
// }) {
//   const [screen, setScreen] =
//     useState("select");

//   const [visibleCount, setVisibleCount] =
//     useState(4);

//   const [questionIndex, setQuestionIndex] =
//     useState(0);

//   const [selected, setSelected] =
//     useState("");

//   const handleNextQuestion = () => {
//     if (
//       questionIndex <
//       QUESTIONS.length - 1
//     ) {
//       setQuestionIndex((p) => p + 1);
//       setSelected("");
//     } else {
//       setScreen("results");
//     }
//   };

//   return (
//     <>
    
//       {screen === "select" && (
//         <AssessmentSelectScreen
//           visibleCount={visibleCount}
//           setVisibleCount={setVisibleCount}
//           onSelect={() =>
//             setScreen("intro")
//           }
//           onBack={onBack}
//         />
//       )}

//       {screen === "intro" && (
//         <AssessmentIntroScreen
//           onContinue={() =>
//             setScreen("questions")
//           }
//           onBack={() =>
//             setScreen("select")
//           }
//         />
//       )}

//       {screen === "questions" && (
//         <AssessmentQuestionScreen
//           questionIndex={questionIndex}
//           selected={selected}
//           setSelected={setSelected}
//           onNext={handleNextQuestion}
//           onPrev={() => {
//             if (questionIndex === 0) {
//               setScreen("intro");
//             } else {
//               setQuestionIndex(
//                 (p) => p - 1
//               );
//             }
//           }}
//         />
//       )}

//       {screen === "results" && (
//         <AssessmentResultsScreen
//           onContinue={() =>
//             setScreen("recommendations")
//           }
//           onBack={() =>
//             setScreen("questions")
//           }
//         />
//       )}

//       {screen === "recommendations" && (
//         <AssessmentRecommendationsScreen
//           onBack={() =>
//             setScreen("results")
//           }
//           onRestart={() => {
//             setScreen("select");
//             setQuestionIndex(0);
//             setSelected("");
//           }}
//         />
//       )}
//     </>
//   );
// }

